import {
  confirmSignIn,
  ConfirmSignInOutput,
  signIn,
  SignInOutput,
} from "aws-amplify/auth";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { TranslationFeKey } from "shared/types/translation-key";

import { useClientMetaData } from "./use-client-meta-data";
import {
  SpecialNavigationRoutes,
  useMamaNavigate,
} from "../../navigation/mama-navigate";
import {
  AuthPagesEnum,
  MAGIC_LINK_SIGN_IN_AUTH_FLOW,
  MAGIC_LINK_SIGN_IN_METHOD,
  SignInNextStepEnum,
} from "../constants";
import { handleCognitoError, MamaCognitoError } from "../utils/cognito-errors";
import { GtmEventAppOpen } from "~/components/app-shell/providers/gtm-events-provider/events";
import { useSendGtmEvents } from "~/components/app-shell/providers/gtm-events-provider/gtm-events-provider";
import { useT } from "~/i18n/use-t";

async function verifyMagicLinkSignIn({
  email,
  code,
  clientMetadata,
}: {
  email: string;
  code: string;
  clientMetadata: Record<string, string>;
}): Promise<
  | { type: "signin"; result: SignInOutput }
  | { type: "confirm"; result: ConfirmSignInOutput }
> {
  const signInResult = await signIn({
    username: decodeURIComponent(email),
    options: {
      authFlowType: MAGIC_LINK_SIGN_IN_AUTH_FLOW,
      autoSignIn: false,
      clientMetadata,
    },
  });

  if (
    !signInResult.isSignedIn &&
    signInResult.nextStep.signInStep ===
      SignInNextStepEnum.CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE
  ) {
    const confirmSignInResult = await confirmSignIn({
      challengeResponse: code,
      options: { clientMetadata },
    });
    return { type: "confirm", result: confirmSignInResult };
  }

  return { type: "signin", result: signInResult };
}

export const useHandleMagicLinkVerification = (): (() => Promise<
  { isSignedIn: boolean } | { error: unknown }
>) => {
  const [params] = useSearchParams();
  const code = useMemo(() => params.get("code"), [params]);
  const urlEncodedEmail = useMemo(() => params.get("email"), [params]);
  const handleWithArgs = useHandleMagicLinkVerificationWithArgs();

  return useCallback(async () => {
    return await handleWithArgs({
      email: urlEncodedEmail ?? "",
      code: code ?? "",
      gtmSource: "magic-link",
    });
  }, [handleWithArgs, urlEncodedEmail, code]);
};

type Args = {
  email: string;
  code: string;
  gtmSource: GtmEventAppOpen["parameters"]["source"];
};

const errorMap: Record<
  string,
  { tx: TranslationFeKey; behavior: "redirect" | "stay" }
> = {
  // Error name hard coded because ErrorClass.name is erased by minification
  ["OtpExpiredError"]: {
    tx: "auth.signInMagic.errors.otpExpired",
    behavior: "redirect",
  },
  ["OtpWrongError"]: {
    tx: "auth.signInMagic.errors.otpWrong",
    behavior: "stay",
  },
  ["OtpNotFoundError"]: {
    tx: "auth.signInMagic.errors.otpNotFound",
    behavior: "redirect",
  },
  ["OtpAlreadyUsedError"]: {
    tx: "auth.signInMagic.errors.otpAlreadyUsed",
    behavior: "redirect",
  },
  ["UserIsThrottledError"]: {
    tx: "auth.signInMagic.errors.otpIsThrottled",
    behavior: "redirect",
  },
} as const;

export const useHandleMagicLinkVerificationWithArgs = (): ((
  args: Args,
) => Promise<
  | { isSignedIn: true; wasAlreadyAuthenticated: boolean }
  | {
      isSignedIn: false;
      error: Error;
      message: string;
      behavior: (typeof errorMap)[keyof typeof errorMap]["behavior"];
    }
>) => {
  const navigate = useMamaNavigate();
  const clientMetadata = useClientMetaData({
    signInMethod: MAGIC_LINK_SIGN_IN_METHOD,
    alreadyHaveMagicLink: "yes",
  });
  const { sendAppOpen } = useSendGtmEvents();
  const t = useT();

  return useCallback(
    async ({ email, code, gtmSource }: Args) => {
      try {
        const { result } = await verifyMagicLinkSignIn({
          email,
          code,
          clientMetadata,
        });

        if (result.isSignedIn === false) {
          throw new Error("Sign-in failed");
        }

        navigate(SpecialNavigationRoutes.HOME);
        sendAppOpen({ source: gtmSource });

        return { isSignedIn: true, wasAlreadyAuthenticated: false };
      } catch (err) {
        const unknownError = {
          tx: "auth.signInMagic.errors.unknown" as TranslationFeKey,
          behavior: "redirect",
        } as const;

        let error = err as Error;

        const match = error.message.match(/\[([^\]]+)\]/);
        if (!match || match.length < 1) {
          const mamaCognitoError = new MamaCognitoError({
            name: unknownError.tx,
          });
          const message = t({ tx: unknownError.tx });
          navigate(AuthPagesEnum.LOGIN_PROBLEM, {
            replace: true,
            error: handleCognitoError(mamaCognitoError, {
              username: email ?? "missing",
            }),
          });
          return {
            isSignedIn: false,
            error: mamaCognitoError,
            message,
            behavior: unknownError.behavior,
          };
        }

        const name = match[1];

        if (name === "UserAlreadyAuthenticatedException") {
          navigate(SpecialNavigationRoutes.HOME);
          return { isSignedIn: true, wasAlreadyAuthenticated: true };
        }

        const { tx, behavior } =
          errorMap[name as keyof typeof errorMap] || unknownError;
        const message = t({ tx });

        if (behavior === "redirect") {
          error = new MamaCognitoError({
            name: tx,
          });

          navigate(AuthPagesEnum.LOGIN_PROBLEM, {
            replace: true,
            error: handleCognitoError(error, {
              username: email ?? "missing",
            }),
          });
        }

        return {
          isSignedIn: false,
          error,
          message,
          behavior,
        };
      }
    },
    [clientMetadata, navigate, sendAppOpen, t],
  );
};
